import type {
  FulfillmentDetails,
  FulfillmentInfo,
  TimeSlotForFulfillment,
} from '@wix/ambassador-restaurants-operations-v1-operation/types';
import { OrderSchedulingType } from '@wix/ambassador-restaurants-operations-v1-operation/types';

import type { TimeSlot } from '../../../types/businessTypes';
import { DispatchType } from '../../../types/businessTypes';

/**
 * True when every item in `arr` exposes the same value at `field` (strict
 * equality). Empty arrays are trivially consistent, and `undefined` shared
 * across all entries also counts as "same".
 */

export const hasSameByField = <T>(field: keyof T, arr: T[]): boolean => {
  if (arr.length === 0) {
    return true;
  }

  const firstValue = arr[0][field];

  return arr.every((item) => item[field] === firstValue);
};

export const convertDate = (date: Date) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
});

export const createTimeSlotId = ({
  startTime,
  endTime,
  maxTimeOptions = 0,
  startsNow = false,
}: {
  startTime: Date | undefined | null;
  endTime: Date | undefined | null;
  maxTimeOptions?: number;
  startsNow?: boolean;
}) =>
  startsNow
    ? `ASAP-${maxTimeOptions}`
    : `${startTime?.toUTCString()}-${endTime?.toUTCString()}-${maxTimeOptions}`;

export const mapFulfilmentTypeToDispatchType = (
  fulfilmentType: string,
): DispatchType => {
  if (fulfilmentType === 'DELIVERY') {
    return DispatchType.DELIVERY;
  }
  if (fulfilmentType === 'DINE_IN') {
    return DispatchType.DINE_IN;
  }

  return DispatchType.PICKUP;
};

function getMinTime(info: FulfillmentInfo): number {
  return info.durationRange
    ? Number(info.durationRange.minDuration)
    : Number(info.maxTime);
}

function getFastestTimeOptionsByFulfillmentInfo(
  arr: FulfillmentInfo[],
): FulfillmentInfo[] {
  const [first, ...rest] = arr;

  if (!first) {
    return [];
  }

  return rest.reduce(
    (fastestOptions, current) => {
      const currentTime = getMinTime(current);
      const fastestTime = getMinTime(fastestOptions[0]);

      if (currentTime < fastestTime) {
        return [current];
      } else if (currentTime === fastestTime) {
        return [...fastestOptions, current];
      }

      return fastestOptions;
    },
    [first] as FulfillmentInfo[],
  );
}

function getSlowestTimeOptionByFulfillmentInfo(
  arr: FulfillmentInfo[],
): FulfillmentInfo {
  const [first, ...rest] = arr;

  if (!first) {
    return {} as FulfillmentInfo;
  }

  return rest.reduce((max, item) => {
    const { durationRange, maxTime: maxTimeOptions } = item;
    const maxTime = max.durationRange
      ? Number(max.durationRange.maxDuration || 0)
      : Number(max.maxTime || 0);

    if (durationRange) {
      return Number(durationRange.maxDuration) > maxTime ? item : max;
    } else {
      return Number(maxTimeOptions) > maxTime ? item : max;
    }
  }, first);
}

function hasSameTime(arr: FulfillmentInfo[]): boolean {
  const [first, ...rest] = arr;

  if (!first) {
    return true;
  }

  return rest.every((item) => {
    if (first.durationRange) {
      return (
        item.durationRange?.minDuration === first.durationRange.minDuration &&
        item.durationRange?.maxDuration === first.durationRange.maxDuration
      );
    }

    return item.maxTime === first.maxTime;
  });
}

const createTimeRangeByFulfillmentInfo = (arr: FulfillmentInfo[]) => {
  const fastestOption = getFastestTimeOptionsByFulfillmentInfo(arr)[0];
  const slowestOption = getSlowestTimeOptionByFulfillmentInfo(arr);
  const minDuration =
    fastestOption.durationRange?.minDuration ?? fastestOption.maxTime;
  const maxDuration =
    slowestOption.durationRange?.maxDuration ?? slowestOption.maxTime;

  if (minDuration === maxDuration) {
    return { maxTimeOptions: minDuration };
  } else {
    return { durationRangeOptions: { minDuration, maxDuration } };
  }
};

const resolveSameMinOrderPriceOption = (fulfillmentInfo: FulfillmentInfo[]) => {
  const cheapest = getMinValueObjects('fee', fulfillmentInfo);
  const fastest = getFastestTimeOptionsByFulfillmentInfo(cheapest);

  return getMinValueObjects('freeFulfillmentPriceThreshold', fastest)[0];
};

const resolveDifferentMinOrderPriceOption = (
  fulfillmentInfo: FulfillmentInfo[],
) => {
  const hasSameFee = hasSameByField('fee', fulfillmentInfo);

  if (hasSameFee && hasSameTime(fulfillmentInfo)) {
    return getMinValueObjects(
      'freeFulfillmentPriceThreshold',
      getMinValueObjects('minOrderPrice', fulfillmentInfo),
    )[0];
  } else {
    const fee = hasSameFee ? fulfillmentInfo[0].fee : undefined;
    const freeFulfillmentPriceThreshold = hasSameByField(
      'freeFulfillmentPriceThreshold',
      fulfillmentInfo,
    )
      ? fulfillmentInfo[0].freeFulfillmentPriceThreshold
      : undefined;

    return {
      fee,
      ...createTimeRangeByFulfillmentInfo(fulfillmentInfo),
      freeFulfillmentPriceThreshold,
    };
  }
};

function getMinValueObjects<T>(key: keyof T, arr: T[]): T[] {
  return arr.slice(1).reduce((min, item) => {
    if (Number(item[key]) < Number(min[0][key])) {
      return [item];
    } else if (Number(item[key]) === Number(min[0][key])) {
      return [...min, item];
    }

    return min;
  }, (arr[0] ? [arr[0]] : []) as T[]);
}

export const resolveFulfillmentInfo = (
  fulfillmentInfo: FulfillmentInfo[],
): FulfillmentInfo => {
  if (hasSameByField('minOrderPrice', fulfillmentInfo)) {
    return resolveSameMinOrderPriceOption(fulfillmentInfo);
  } else {
    return resolveDifferentMinOrderPriceOption(fulfillmentInfo);
  }
};

export const processTimeSlotForFulfillment = (
  fulfillmentTimeSlot: TimeSlotForFulfillment,
): TimeSlot | undefined => {
  const { timeSlot, fulfilmentType, fulfillmentInfo } = fulfillmentTimeSlot;
  const { startTime, endTime, orderSchedulingType } = timeSlot ?? {};

  if (!startTime || !endTime || !fulfillmentInfo?.length || !fulfilmentType) {
    return undefined;
  }

  const selectedFulfillmentInfo = resolveFulfillmentInfo(fulfillmentInfo);
  const fulfillmentDetails: FulfillmentDetails = {
    maxTimeOptions: selectedFulfillmentInfo.maxTime,
    durationRangeOptions: selectedFulfillmentInfo.durationRange,
    ...selectedFulfillmentInfo,
  };

  const startsNow = orderSchedulingType === OrderSchedulingType.ASAP;

  return {
    id: createTimeSlotId({ startTime, endTime, startsNow }),
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
    dispatchType: mapFulfilmentTypeToDispatchType(fulfilmentType),
    startsNow,
    fulfillmentDetails,
  };
};

export const mergeASAPTimeSlotsForFulfillment = (
  fulfillmentTimeSlots: TimeSlotForFulfillment[],
) => {
  const asapTimeSlots = fulfillmentTimeSlots.filter(
    (slot) => slot.timeSlot?.orderSchedulingType === OrderSchedulingType.ASAP,
  );

  if (asapTimeSlots.length <= 1) {
    return fulfillmentTimeSlots;
  }
  const fulfillmentInfo = asapTimeSlots
    .map((slot) => slot.fulfillmentInfo ?? [])
    .flat();
  const mergedTimeSlot: TimeSlotForFulfillment = {
    ...asapTimeSlots[asapTimeSlots.length - 1],
    fulfillmentInfo,
  };
  const timeSlots = fulfillmentTimeSlots.filter(
    (slot) => slot.timeSlot?.orderSchedulingType !== OrderSchedulingType.ASAP,
  );

  timeSlots.push(mergedTimeSlot);

  return timeSlots;
};
